/**
 * GENERATED from source/challenge-point.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const CHALLENGE_POINT = {
  "key": "challenge-point",
  "title": "Challenge Point & Desirable Difficulties",
  "category": "Concepts",
  "platform": {
    "key": "challenge-point",
    "sub": "Hard Is Not the Goal. Productive Struggle Is.",
    "duration": "8–10 min read",
    "chapter": {
      "id": "challenge-point",
      "title": "Challenge Point & Desirable Difficulties"
    }
  },
  "cert": {
    "key": "challenge-point-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "intro",
      "title": "Challenge Point",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Challenge Point"
        },
        {
          "kind": "text",
          "md": "Practice needs enough difficulty to create a reason for the athlete to adapt.\n\nIf everything works exactly as expected, there is very little information telling the system:\n\n**Something needs to change.**\n\nThat is why failure matters.\n\nNot because failure is the only way humans learn.\n\nBut because error is one of the clearest signals that the current solution is not good enough yet."
        },
        {
          "kind": "bigIdea",
          "md": "Learning needs a reason to change."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Scientific Clarification",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Motor learning is not driven by one single mechanism.\n\nHumans can learn through:\n\n- sensory prediction error,\n- reinforcement,\n- explicit strategies,\n- use-dependent processes,\n- repetition,\n- and other interacting mechanisms.\n\nThe cerebellum plays an important role in error-based motor learning, especially when the actual outcome differs from the predicted outcome.\n\nA common misconception is:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Failure is the only way learning happens.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "That isn't how motor learning works. A more accurate takeaway is:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Error is one of the strongest signals that the current solution needs updating.",
          "depth": "cert"
        },
        {
          "kind": "podcastLink",
          "title": "The Science Behind How We ACTUALLY Learn",
          "url": "https://podcasts.apple.com/us/podcast/the-science-behind-how-we-actually-learn/id1604852096?i=1000773754071",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Why Easy Practice Can Be Misleading"
        },
        {
          "kind": "text",
          "md": "Clean practice feels good.\n\nThe athlete looks sharp.\n\nThe coach gets a nice dopamine hit.\n\nSuccess rate is high.\n\nBut high success can sometimes mean:"
        },
        {
          "kind": "bigIdea",
          "md": "The athlete is not being asked to solve enough."
        },
        {
          "kind": "text",
          "md": "If the task is so easy that the athlete already owns the solution, repetition may still have a purpose:\n\n- confidence,\n- warm-up,\n- calibration,\n- volume,\n- or preparation.\n\nBut it may not be creating much new learning.\n\nThat is the paradox:"
        },
        {
          "kind": "bigIdea",
          "md": "Practice can look better while producing less learning pressure."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Math Analogy"
        },
        {
          "kind": "text",
          "md": "Imagine somebody learning advanced math.\n\nIf they already understand algebra, repeatedly solving:\n\n**5 × 5**\n\nwill not challenge them much.\n\nBut handing them an extremely advanced equation they cannot even begin to understand is not useful either.\n\nGood learning usually lives somewhere between:\n\n**too easy to require adaptation**\n\nand\n\n**too hard to extract useful information from.**\n\nMotor learning is not the same as classroom math.\n\nBut the analogy captures the coaching problem:"
        },
        {
          "kind": "bigIdea",
          "md": "Challenge has to match the learner."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Nominal Difficulty vs. Functional Difficulty"
        },
        {
          "kind": "text",
          "md": "This is one of the most useful ideas from the Challenge Point Framework."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Nominal difficulty"
        },
        {
          "kind": "text",
          "md": "How difficult the task is **on paper**."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Functional difficulty"
        },
        {
          "kind": "text",
          "md": "How difficult the task actually is **for this athlete, under these conditions, right now**.\n\nThe exact same drill can be:\n\n- easy for one athlete,\n- appropriate for another,\n- overwhelming for another.\n\nThat is why labels like:\n\n**beginner drill**\n\n**intermediate drill**\n\n**advanced drill**\n\nare often incomplete."
        },
        {
          "kind": "bigIdea",
          "md": "Challenge does not live in the drill. It lives in the interaction between the drill and the athlete."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Challenge Point Framework",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The Challenge Point Framework proposes that learning depends on the relationship between:\n\n- the athlete's skill level,\n- the nominal difficulty of the task,\n- and the functional difficulty experienced during practice.\n\nAs functional difficulty increases, the athlete may receive more information that can support learning.\n\nBut only to a point.\n\nIf the task becomes too difficult, the learner may no longer be able to use the available information effectively.\n\nThis creates an inverted-U style idea:\n\n**Too little challenge → limited learning pressure**\n\n**Appropriate challenge → useful information and adaptation**\n\n**Too much challenge → overload, random failure, or disengagement**\n\nThe exact optimal point is not universal.\n\nIt changes with the athlete, task, environment, and goal.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "harder",
      "title": "Harder Is Not Always Better",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Harder Is Not Always Better"
        },
        {
          "kind": "text",
          "md": "A coach can always make something harder.\n\nAdd:\n\n- more speed,\n- less space,\n- more defenders,\n- more decisions,\n- more fatigue,\n- more pressure,\n- more movement,\n- more interference.\n\nThat does not mean the athlete is learning more."
        },
        {
          "kind": "bigIdea",
          "md": "Hard is not the goal. Productive struggle is."
        },
        {
          "kind": "text",
          "md": "The question is not:\n\n**How hard can I make this?**\n\nIt is:"
        },
        {
          "kind": "bigIdea",
          "md": "How much difficulty creates useful adaptation without destroying the learning target?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Most Important BAM Question:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "What Is Actually Failing?"
        },
        {
          "kind": "text",
          "md": "A low success rate does not automatically mean the drill is challenging the right thing.\n\nThis is where coaches have to look one layer deeper.\n\nImagine the goal of an activity is finishing touch.\n\nBut the athlete has to complete a difficult ball-handling sequence first.\n\nIf they keep losing the ball before the finish, the drill has plenty of difficulty.\n\nBut the finishing skill may barely be challenged at all.\n\nThe failure is happening in the wrong place."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: Jumping Coordination vs. Touch"
        },
        {
          "kind": "text",
          "md": "Imagine the learning target is a left-right jump pattern into a finish.\n\nThe athlete executes the jump smoothly every time.\n\nBut they miss the layup because their touch is poor.\n\nFrom the outside:\n\n**The rep failed.**\n\nBut the learning target succeeded.\n\nNow flip it.\n\nThe athlete makes the shot but repeatedly organizes the jump poorly.\n\nFrom a scoring standpoint:\n\n**Success.**\n\nFrom the actual learning target:\n\n**Failure.**\n\nThat gives us a core BAM diagnostic:"
        },
        {
          "kind": "bigIdea",
          "md": "Don't just ask whether the rep failed. Ask what failed."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Targeted Difficulty"
        },
        {
          "kind": "text",
          "md": "Practice needs difficulty.\n\nBut the difficulty should live as close as possible to the quality we actually want to develop.\n\nIf the target is:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Touch"
        },
        {
          "kind": "text",
          "md": "Challenge touch."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Balance"
        },
        {
          "kind": "text",
          "md": "Challenge balance."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Decision-making"
        },
        {
          "kind": "text",
          "md": "Challenge the decision."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Ball control"
        },
        {
          "kind": "text",
          "md": "Challenge ball control."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Jump coordination"
        },
        {
          "kind": "text",
          "md": "Challenge the jump."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Physical capacity"
        },
        {
          "kind": "text",
          "md": "Challenge physical capacity.\n\nThe whole drill may be difficult.\n\nBut only some parts of that difficulty may be relevant to the learning target."
        },
        {
          "kind": "bigIdea",
          "md": "Challenge the thing you actually want to change."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Types of Difficulty"
        },
        {
          "kind": "text",
          "md": "You do not need to classify every drill perfectly.\n\nBut it helps to understand where the challenge is coming from."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Execution Difficulty"
        },
        {
          "kind": "text",
          "md": "Can the athlete physically coordinate the action?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Perceptual / Decision Difficulty"
        },
        {
          "kind": "text",
          "md": "Can they detect the information and select a useful solution?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Information Load"
        },
        {
          "kind": "text",
          "md": "How much information has to be processed?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Physical Difficulty"
        },
        {
          "kind": "text",
          "md": "How much speed, force, fatigue, or workload is involved?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Psychological Difficulty"
        },
        {
          "kind": "text",
          "md": "How much pressure, consequence, confidence, or frustration is involved?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Practice-Organization Difficulty"
        },
        {
          "kind": "text",
          "md": "Does the athlete have to reconstruct the solution instead of repeating it?\n\nExamples include contextual interference, spacing, or reduced predictability.\n\nThe point is simple:"
        },
        {
          "kind": "bigIdea",
          "md": "Know which system you're actually challenging."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Desirable Difficulties",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A **desirable difficulty** is a practice condition that makes performance harder in the short term while improving learning outcomes such as retention or transfer.\n\nThe key word is:\n\n**desirable.**\n\nDifficulty itself is not automatically useful.\n\nSome forms of difficulty can improve learning.\n\nOthers simply create confusion, fatigue, or failure.\n\nThis is why the coach has to distinguish:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Difficulty that creates useful learning work",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "from",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Difficulty that merely makes the task harder.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Performance Is Not Learning",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "One of the most important ideas in skill acquisition:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Performance during practice is not the same thing as learning.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A practice condition can make the athlete look worse today while producing better retention later.\n\nExamples from learning research can include:\n\n- spacing,\n- interleaving,\n- contextual interference,\n- reduced feedback,\n- variable conditions,\n- and retrieval demands.\n\nBut none of these are universally better.\n\nThe effect depends on the learner, task, and conditions.\n\nA clean practice session can overestimate learning.\n\nAn ugly session can underestimate it.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "success",
      "title": "Success Rate as a Coaching Clue",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Success Rate as a Coaching Clue"
        },
        {
          "kind": "text",
          "md": "Success rate can be one of the simplest ways to estimate challenge.\n\nBAM often likes a general range around:"
        },
        {
          "kind": "bigIdea",
          "md": "40–70% success"
        },
        {
          "kind": "text",
          "md": "when success is a meaningful measure of the learning target.\n\nThat is a **BAM coaching heuristic**, not a universal scientific rule.\n\nThe important part is defining success correctly.\n\nIf the target is the decision:\n\n**Did they make the right decision?**\n\nIf the target is balance:\n\n**Did they get on balance?**\n\nIf the target is a jump pattern:\n\n**Did they execute the jump correctly?**\n\nScoring the ball may not be the success metric."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Success Rate Is a Clue, Not the Definition"
        },
        {
          "kind": "text",
          "md": "A player can have a high success rate and still be doing difficult work.\n\nSometimes they are simply performing at an unusually high level.\n\nThey may be:\n\n- highly focused,\n- physically primed,\n- confident,\n- in rhythm,\n- or in a flow-like state.\n\nOne strong session does not perfectly represent the athlete's true level.\n\nPerformance fluctuates.\n\nSo:"
        },
        {
          "kind": "bigIdea",
          "md": "Use success rate as a strong clue, not as the definition of challenge."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "When the Athlete Is Flying"
        },
        {
          "kind": "text",
          "md": "Sometimes the athlete is dominating a task that normally challenges them.\n\nThat can be a useful moment to push.\n\nIf they are:\n\n- well-rested,\n- locked in,\n- highly confident,\n- and performing above baseline,\n\nyou may increase difficulty and test the edge of their ability.\n\nThink of it like strength and conditioning.\n\nIf the athlete feels unusually good that day, sometimes you take advantage of it.\n\nBut not always."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Sometimes You Leave the Flow State Alone"
        },
        {
          "kind": "text",
          "md": "Context matters.\n\nIf the athlete has been struggling for several sessions and a game is approaching, you may decide:"
        },
        {
          "kind": "bigIdea",
          "md": "Do not crank the difficulty. Let them feel successful."
        },
        {
          "kind": "text",
          "md": "Confidence can be part of the training goal.\n\nChallenge should be periodized.\n\nThe same athlete may need:\n\n- more failure during a development block,\n- less failure close to competition,\n- more challenge when confidence is high,\n- or more manageable success when confidence is fragile.\n\nThat is the art of coaching."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Psychological Challenge Is Part of Functional Difficulty",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Two athletes can complete the exact same task with the exact same success rate and experience very different functional difficulty.\n\nOne athlete interprets failure as:\n\n**information.**\n\nThe other interprets it as:\n\n**proof that they cannot do the skill.**\n\nThat changes the practice experience.\n\nA coach should consider:\n\n- frustration tolerance,\n- confidence,\n- competitive personality,\n- previous experience,\n- current emotional state,\n- and timing within the season.\n\nThis does not mean protecting athletes from failure.\n\nAthletes need to develop a **callus for failure**.\n\nBut the exposure can be progressed.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Build a Callus for Failure"
        },
        {
          "kind": "text",
          "md": "If an athlete disengages whenever they struggle, avoiding struggle forever does not solve the problem.\n\nThey need experience:\n\n- failing,\n- adjusting,\n- trying again,\n- and learning that failure is information.\n\nBut this can be progressed like any other demand.\n\nYou may start with manageable challenge.\n\nThen gradually increase:\n\n- uncertainty,\n- consequences,\n- difficulty,\n- pressure,\n- or failure exposure."
        },
        {
          "kind": "bigIdea",
          "md": "The athlete should learn how to stay engaged when the solution is not immediately available."
        }
      ]
    },
    {
      "id": "starting",
      "title": "Starting Too Hard Can Be Useful",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Starting Too Hard Can Be Useful"
        },
        {
          "kind": "text",
          "md": "BAM does not believe every task should start at the athlete's perfect challenge point.\n\nSometimes it is useful to begin with a version that is probably too difficult.\n\nWhy?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. It can create attention"
        },
        {
          "kind": "bigIdea",
          "md": "\"Oh. I'm not good at this yet.\""
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. It can humble the athlete"
        },
        {
          "kind": "text",
          "md": "The task reveals a gap they may not have recognized."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. It can help the coach calibrate"
        },
        {
          "kind": "text",
          "md": "If you do not know the athlete's level, let them prove the harder version is too much before assuming they cannot handle it.\n\nA useful BAM bias:"
        },
        {
          "kind": "bigIdea",
          "md": "When uncertain, sometimes start ambitious and work backward."
        },
        {
          "kind": "text",
          "md": "This is a tool, not a universal rule."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Regress Without Shame"
        },
        {
          "kind": "text",
          "md": "If the harder version produces:\n\n- random failure,\n- confusion,\n- disengagement,\n- or no meaningful exposure to the target skill,\n\nregress it.\n\nThat is not lowering standards.\n\nIt is finding a challenge the athlete can actually learn from."
        },
        {
          "kind": "bigIdea",
          "md": "Too hard is only useful if the athlete can still see a path toward the solution."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A BAM Success-Rate Heuristic"
        },
        {
          "kind": "text",
          "md": "For many skill tasks:"
        },
        {
          "kind": "bigIdea",
          "md": "40–70% success can be a useful starting range."
        },
        {
          "kind": "text",
          "md": "But there are many valid reasons to work outside it."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Higher success"
        },
        {
          "kind": "text",
          "md": "Useful for:\n\n- confidence,\n- grooving,\n- warm-up,\n- calibration,\n- technical stabilization,\n- pre-game preparation."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Lower success"
        },
        {
          "kind": "text",
          "md": "Useful for:\n\n- exploration,\n- overload,\n- testing the edge,\n- high-difficulty decisions,\n- advanced problem solving.\n\nDo not worship the number.\n\nUse it to ask better questions."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Shooting Is a Special Case"
        },
        {
          "kind": "text",
          "md": "Shooting success should often reflect the actual problem.\n\nIf an athlete is roughly a 40% game shooter from a specific shot type, practice where they make 85% may be solving a very different task.\n\nBAM often likes much of shooting practice to live near a **representative success range**.\n\nFor a shot the athlete makes around 40% in games, that might mean much of training lands around:"
        },
        {
          "kind": "bigIdea",
          "md": "30–50%"
        },
        {
          "kind": "text",
          "md": "depending on the purpose.\n\nThere are still times for:\n\n- 60–70%,\n- 20%,\n- or anything else.\n\nThe goal is not to force one percentage.\n\nIt is to understand what the percentage tells you about the task.\n\nThis is a BAM coaching model, not a universal shooting-science prescription."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Practical BAM Diagnostic"
        },
        {
          "kind": "text",
          "md": "When a task looks too easy or too hard, ask:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. What is failing?"
        },
        {
          "kind": "text",
          "md": "Not:\n\n**Did the rep fail?**\n\nBut:\n\n**Which part failed?**"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. Why is it failing?"
        },
        {
          "kind": "text",
          "md": "Is the problem:\n\n- execution,\n- perception,\n- decision-making,\n- fatigue,\n- confidence,\n- information overload,\n- or something else?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. Is that the thing I wanted to challenge?"
        },
        {
          "kind": "text",
          "md": "If not, the difficulty may be misplaced."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "4. Can the athlete still learn from the error?"
        },
        {
          "kind": "text",
          "md": "Can they:\n\n- detect what happened,\n- make an adjustment,\n- stay engaged,\n- and move toward a better solution?\n\nIf the answer is no, the task may be too difficult."
        }
      ]
    },
    {
      "id": "activity1",
      "title": "ACTIVITY 1: What Failed?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 1: What Failed?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "failed",
          "title": false,
          "numbered": false,
          "intro": "Take one drill you coach regularly.\n\nPick a recent rep that looked unsuccessful.\n\nAnswer:",
          "steps": [
            {
              "id": "drill",
              "q": "Did the drill fail?",
              "hint": "Or did one component fail?",
              "rows": 3
            },
            {
              "id": "what",
              "q": "What exactly failed?",
              "hint": "- decision?\n- balance?\n- touch?\n- ball control?\n- timing?\n- physical capacity?",
              "rows": 3
            },
            {
              "id": "target",
              "q": "Was that the actual learning target?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "If not:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "The drill may have been hard in the wrong place.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 2: Nominal vs. Functional Difficulty",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "athletes",
          "title": false,
          "numbered": false,
          "intro": "Choose one drill.\n\nThe drill has the same nominal difficulty.\n\nFor each athlete:\n\n- What makes the functional difficulty different?\n- What would likely create overload?\n- What would likely be too easy?\n- What would you change first?\n\nNow imagine:",
          "steps": [
            {
              "id": "a",
              "q": "Athlete A",
              "hint": "A beginner.",
              "rows": 3
            },
            {
              "id": "b",
              "q": "Athlete B",
              "hint": "An intermediate athlete.",
              "rows": 3
            },
            {
              "id": "c",
              "q": "Athlete C",
              "hint": "An advanced athlete.",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 3: Find the Right Success Measure",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "measure",
          "title": false,
          "numbered": false,
          "intro": "Choose an activity.\n\nDo not use scoring percentage automatically.\n\nDefine:",
          "steps": [
            {
              "id": "target",
              "q": "What is the actual learning target?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "metric",
              "q": "Then define the binary success metric.",
              "hint": "Examples:\n\n**Target: balance**\n\nSuccess = on-balance landing / organization.\n\n**Target: decision**\n\nSuccess = appropriate decision.\n\n**Target: touch**\n\nSuccess = finish outcome / touch target.\n\n**Target: jumping coordination**\n\nSuccess = correct jump organization.",
              "rows": 3
            },
            {
              "id": "rate",
              "q": "Now estimate the success rate.",
              "hint": "Is the task actually as difficult as you thought?",
              "rows": 3
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity4",
      "title": "ACTIVITY 4: Productive or Pointless Difficulty?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 4: Productive or Pointless Difficulty?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "productive",
          "title": false,
          "numbered": false,
          "intro": "Take one way you commonly make practice harder.\n\nExamples:\n\n- less space\n- more speed\n- more defenders\n- more fatigue\n- more decision load\n- more interference\n\nAnswer:",
          "steps": [
            {
              "id": "system",
              "q": "What system does this difficulty challenge?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "want",
              "q": "Is that the system I want to improve?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "error",
              "q": "Does it create useful error?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "adjust",
              "q": "Can the athlete still make meaningful adjustments?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "engaged",
              "q": "Is the athlete staying engaged?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "If not:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "The practice may be hard without being productively hard.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 5: Periodize the Challenge",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "periodize",
          "title": false,
          "numbered": false,
          "intro": "Choose one athlete.\n\nImagine three moments:",
          "steps": [
            {
              "id": "early",
              "q": "Early development block",
              "hint": "How much failure are you comfortable with?",
              "rows": 3
            },
            {
              "id": "mid",
              "q": "Mid-block",
              "hint": "How would you progress challenge?",
              "rows": 3
            },
            {
              "id": "close",
              "q": "Close to competition",
              "hint": "Would you keep the same failure rate?\n\nWhy or why not?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Then consider the athlete's personality.",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "personality",
          "prompt": "Would you make the same choices for:",
          "hint": "- a highly confident athlete,\n- and an athlete whose confidence is currently fragile?",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "map",
      "title": "A Simple Challenge Map",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Simple Challenge Map"
        },
        {
          "kind": "framework",
          "steps": [
            {
              "q": "HIGH SUCCESS + LOW TARGET DIFFICULTY",
              "md": "Likely under-challenged.\nConsider: progress the target skill."
            },
            {
              "q": "LOW SUCCESS + FAILURE ON THE TARGET SKILL",
              "md": "Potentially productive.\nAsk: can the athlete detect and adjust?"
            },
            {
              "q": "LOW SUCCESS + FAILURE ON THE WRONG COMPONENT",
              "md": "Misplaced difficulty.\nConsider: simplify the interfering component."
            },
            {
              "q": "VERY LOW SUCCESS + RANDOM FAILURE",
              "md": "Likely too hard.\nConsider: regress."
            },
            {
              "q": "HIGH SUCCESS + HIGH TRUE DIFFICULTY",
              "md": "Possible high-performance / flow-like state.\nConsider: push the edge or preserve the confidence depending on context."
            }
          ]
        },
        {
          "kind": "reflection",
          "md": "Pick one activity you ran recently.\n\nDo not ask:\n\n**Was it hard?**\n\nAsk:\n\n**What was hard about it?**\n\nThen:\n\n**Was that the thing I actually wanted the athlete to improve?**",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Choose one skill you coach often.\n\nAnswer:",
          "steps": [
            {
              "id": "easy",
              "q": "What does too easy look like?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "productive",
              "q": "What does productive struggle look like?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "random",
              "q": "What does random failure look like?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "metric",
              "q": "What success metric actually represents the skill?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "source",
              "q": "Where does difficulty usually come from?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "overuse",
              "q": "What kind of difficulty do you accidentally overuse?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "personality",
              "q": "How does the athlete's personality affect the ideal challenge point?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "gameweek",
              "q": "How would your answer change one week before an important game?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Finally:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Am I making practice harder, or am I making the target skill more learnable?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "Humans need challenge to adapt.\n\nBut difficulty is only useful when it creates the right kind of problem.\n\nA coach should not chase:\n\n**easy**\n\nor\n\n**hard.**\n\nThe coach should chase:"
        },
        {
          "kind": "bigIdea",
          "md": "productive struggle."
        },
        {
          "kind": "text",
          "md": "That means enough failure to create information and adaptation.\n\nBut not so much failure that:\n\n- the athlete loses the problem,\n- disengages,\n- or spends the entire drill failing at something other than the learning target.\n\nUse success rate as a clue.\n\nDefine success around the thing you actually care about.\n\nPeriodize difficulty.\n\nAnd always ask:"
        },
        {
          "kind": "bigIdea",
          "md": "What failed? Why did it fail? Was that the thing I wanted to challenge? Can the athlete learn from it?"
        },
        {
          "kind": "text",
          "md": "Because:"
        },
        {
          "kind": "bigIdea",
          "md": "Hard is not the goal. Productive struggle is."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Suggested References",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "- Guadagnoli, M. A., & Lee, T. D. (2004). Challenge Point: A Framework for Conceptualizing the Effects of Various Practice Conditions in Motor Learning. Journal of Motor Behavior.\n- Bjork, R. A. and colleagues — work on desirable difficulties and the distinction between short-term performance and long-term learning.\n- Krakauer, J. W., Hadjiosif, A. M., Xu, J., Wong, A. L., & Haith, A. M. (2019). Motor Learning. Comprehensive review of multiple mechanisms of motor learning.\n- Research on sensory prediction error and cerebellar contributions to adaptation.\n- Research on contextual interference, spacing, retrieval, and feedback frequency as practice manipulations that can sometimes create desirable difficulty.",
          "depth": "cert"
        },
        {
          "kind": "podcastLink",
          "title": "The Science Behind How We ACTUALLY Learn",
          "url": "https://podcasts.apple.com/us/podcast/the-science-behind-how-we-actually-learn/id1604852096?i=1000773754071",
          "depth": "platform"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            },
            {
              "key": "degrees-of-freedom",
              "title": "Degrees of Freedom",
              "blurb": "Freedom Is Only Useful If You Can Organize It."
            }
          ]
        }
      ]
    }
  ]
};
